import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Typography, Box, Paper, Divider,
  Button, CircularProgress, Alert, Tabs, Tab
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import {
  getFirestore, collection, query, where,
  orderBy, getDocs
} from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';

// 辅助函数：根据订单信息获取总金额
const getOrderTotal = (order) => {
  // 检查多种可能的订单金额存储位置
  if (order.totalAmount) {
    return parseFloat(order.totalAmount).toFixed(2);
  }
  
  if (order.amount?.total) {
    return parseFloat(order.amount.total).toFixed(2);
  }
  
  if (order.amount?.value) {
    return parseFloat(order.amount.value).toFixed(2);
  }
  
  // 如果订单包含PayPal支付信息
  if (order.captureDetails?.purchase_units?.[0]?.amount?.value) {
    return parseFloat(order.captureDetails.purchase_units[0].amount.value).toFixed(2);
  }
  
  // 尝试通过商品计算总额
  if (order.items && Array.isArray(order.items)) {
    const total = order.items.reduce((sum, item) => {
      const price = parseFloat(item.price) || 0;
      const quantity = parseInt(item.quantity) || 1;
      return sum + (price * quantity);
    }, 0);
    return total.toFixed(2);
  }
  
  return '0.00';
};

const OrderHistory = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { clearCart, addItem } = useCart();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!currentUser) {
        navigate('/login');
        return;
      }

      try {
        const db = getFirestore();
        const ordersRef = collection(db, 'orders');
        const q = query(
          ordersRef,
          where('userId', '==', currentUser.uid),
          orderBy('createdAt', 'desc')
        );

        const snapshot = await getDocs(q);
        const ordersData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date()
        }));

        setOrders(ordersData);
      } catch (err) {
        console.error('获取订单失败:', err);
        setError('获取订单数据失败');
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [currentUser, navigate]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleViewDetail = (orderId) => {
    navigate(`/order/${orderId}`);
  };

  const handlePayment = async (orderId) => {
    try {
      setLoading(true);
      setError('');
      
      // 1. 获取订单详情
      const order = orders.find(o => o.id === orderId);
      if (!order || !order.items || order.items.length === 0) {
        setError('无法获取订单项目');
        setLoading(false);
        return;
      }

      // 2. 清空当前购物车
      clearCart();

      // 3. 添加订单中的商品到购物车
      order.items.forEach(item => {
        // 创建与购物车兼容的商品项
        const cartItem = {
          id: item.productId || item.id,
          name: item.name,
          price: parseFloat(item.price),
          quantity: item.quantity || 1,
          imageUrl: item.imageUrl || item.image
        };
        
        addItem(cartItem, cartItem.quantity);
      });

      // 4. 导航到结账页面
      setLoading(false);
      navigate('/checkout', { state: { orderId, fromOrderHistory: true } });
    } catch (err) {
      console.error('准备支付失败:', err);
      setError('准备支付过程中出错');
      setLoading(false);
    }
  };

  const handleBackToUser = () => {
    navigate('/user');
  };

  const filterOrders = () => {
    if (activeTab === 0) {
      return orders; // 全部订单
    } else if (activeTab === 1) {
      return orders.filter(order => order.status === 'CREATED'); // 待付款
    } else if (activeTab === 2) {
      return orders.filter(order => order.status === 'PROCESSING' || order.status === 'SHIPPED'); // 处理中
    } else if (activeTab === 3) {
      return orders.filter(order => order.status === 'COMPLETED'); // 已完成
    } else if (activeTab === 4) {
      return orders.filter(order => order.status === 'CANCELLED'); // 已取消
    }
    return orders;
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  const filteredOrders = filterOrders();

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button 
        startIcon={<ArrowBackIcon />}
        onClick={handleBackToUser}
        sx={{ mb: 3 }}
      >
        返回个人中心
      </Button>

      <Typography variant="h4" gutterBottom>我的订单</Typography>

      <Tabs 
        value={activeTab} 
        onChange={handleTabChange} 
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3 }}
      >
        <Tab label="全部订单" />
        <Tab label="待付款" />
        <Tab label="处理中" />
        <Tab label="已完成" />
        <Tab label="已取消" />
      </Tabs>

      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : filteredOrders.length === 0 ? (
        <Alert severity="info">暂无订单记录</Alert>
      ) : (
        filteredOrders.map(order => (
          <Paper key={order.id} sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle1">订单号: {order.id}</Typography>
              <Typography color="primary" fontWeight="bold">
                {order.status === 'COMPLETED' && '已完成'}
                {order.status === 'CREATED' && '待付款'}
                {order.status === 'PROCESSING' && '处理中'}
                {order.status === 'SHIPPED' && '已发货'}
                {order.status === 'CANCELLED' && '已取消'}
              </Typography>
            </Box>

            <Typography variant="body2" color="textSecondary">
              下单时间: {new Date(order.createdAt).toLocaleString()}
            </Typography>

            <Divider sx={{ my: 1 }} />

            {order.items && order.items.map((item, index) => (
              <Box key={index} sx={{ display: 'flex', my: 1 }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography>{item.name}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    数量: {item.quantity}
                  </Typography>
                </Box>
                <Typography>${parseFloat(item.price).toFixed(2)}</Typography>
              </Box>
            ))}

            <Divider sx={{ my: 1 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography>总计:</Typography>
              <Typography fontWeight="bold">
                ${getOrderTotal(order)}
              </Typography>
            </Box>

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button 
                variant="outlined" 
                size="small"
                sx={{ mr: 1 }}
                onClick={() => handleViewDetail(order.id)}
              >
                查看详情
              </Button>

              {order.status === 'CREATED' && (
                <Button 
                  variant="contained" 
                  color="primary"
                  size="small"
                  onClick={() => handlePayment(order.id)}
                >
                  立即付款
                </Button>
              )}
            </Box>
          </Paper>
        ))
      )}
    </Container>
  );
};

export default OrderHistory;